import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { useEffect, useState } from "react";
import {
  approveMcpOAuthInteraction,
  denyMcpOAuthInteraction,
  getMcpOAuthInteraction,
  listOrganizations,
  MCP_OAUTH_DEV_ISSUER,
  MCP_OAUTH_DEV_REDIRECT_HOST,
  MCP_RESOURCE_URI,
  type McpOAuthInteraction,
} from "@/lib/api";
import { safeMcpOAuthCallbackRedirect } from "@/lib/return-to";
import { useSession } from "@/lib/session";

type Organization = { id: string; name: string; slug: string };

const SAFE_ERROR = "This authorization request could not be completed. Return to the MCP client and try again.";

export function McpOAuthConsentPage({ interactionId }: { interactionId: string }) {
  const { state } = useSession();
  const [interaction, setInteraction] = useState<McpOAuthInteraction | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [returned, setReturned] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setInteraction(null);
    setOrganizations([]);
    setOrganizationId("");
    void (async () => {
      try {
        const request = await getMcpOAuthInteraction(interactionId);
        if (
          request.scope !== "build:read" ||
          request.resource_uri !== MCP_RESOURCE_URI ||
          request.issuer !== MCP_OAUTH_DEV_ISSUER ||
          request.redirect_host !== MCP_OAUTH_DEV_REDIRECT_HOST ||
          !request.client_name.trim()
        ) {
          throw new Error("Unexpected authorization request");
        }
        const listedOrganizations = await listOrganizations();
        if (cancelled) return;
        const options: Organization[] = listedOrganizations.map((item) => ({
          id: item.id,
          name: item.name,
          slug: item.slug,
        }));
        setInteraction(request);
        setOrganizations(options);
      } catch {
        if (!cancelled) setError("This authorization request could not be verified. Return to the MCP client and try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [interactionId, state]);

  const returnToClient = (url: string) => {
    const safeUrl = safeMcpOAuthCallbackRedirect(url, interaction?.issuer);
    if (!safeUrl) {
      setError(SAFE_ERROR);
      return;
    }
    setReturned(true);
    window.location.assign(safeUrl);
  };

  const approve = async () => {
    if (
      !interaction || !organizationId || busy ||
      !organizations.some((item) => item.id === organizationId)
    ) return;
    setBusy(true);
    setError(null);
    try {
      const result = await approveMcpOAuthInteraction(interactionId, organizationId);
      returnToClient(result.redirect_url);
    } catch {
      setError(SAFE_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const deny = async () => {
    if (!interaction || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await denyMcpOAuthInteraction(interactionId);
      returnToClient(result.redirect_url);
    } catch {
      setError(SAFE_ERROR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-2xl items-center px-5 py-12">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>MCP access request</CardTitle>
          <CardDescription>
            Review the requested access. Only the selected organization will be available.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          {loading ? <p role="status">Loading authorization request…</p> : null}
          {error ? <p role="alert" className="text-sm text-[var(--danger)]">{error}</p> : null}
          {returned ? <p role="status">Returning to the MCP client…</p> : null}
          {!loading && interaction ? (
            <>
              <dl className="grid gap-3 text-sm">
                <div><dt className="font-medium">Client</dt><dd>{interaction.client_name}</dd></div>
                <div><dt className="font-medium">Callback hostname</dt><dd><code>{interaction.redirect_host}</code></dd></div>
                <div><dt className="font-medium">Access requested</dt><dd><code>{interaction.scope}</code> · read-only</dd></div>
                <div><dt className="font-medium">Resource</dt><dd className="break-all"><code>{interaction.resource_uri}</code></dd></div>
              </dl>
              <p className="text-sm text-[var(--muted-foreground)]">
                Choose one organization. SYLVIQ checks your current membership when you approve and again when the client uses its token.
              </p>
              <label className="grid gap-1.5 text-sm font-medium">
                Organization
                <Select
                  aria-label="Organization for MCP access"
                  disabled={busy}
                  onChange={setOrganizationId}
                  options={[{ value: "", label: "Choose an organization" }, ...organizations.map((item) => ({ value: item.id, label: item.name }))]}
                  value={organizationId}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  aria-label="Allow read-only access"
                  disabled={busy || !organizationId || !interaction}
                  onClick={() => void approve()}
                >
                  {busy ? "Working…" : "Allow read-only access"}
                </Button>
                <Button aria-label="Deny MCP access" disabled={busy} onClick={() => void deny()} variant="outline">
                  Deny
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}
